import {
  createContext,
  FunctionComponent,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import {
  ReactConsentContext,
  useSelectedWorkspace,
  useInfo,
} from '../../Flow/ConsentContextProvider';
import { useAnalytics } from '../../Analytics/AnalyticsProvider';
import { useFeatureGate } from '@atlassian/feature-gates-react';
import { OAuthConsentFeatureGate } from '../../FeatureGates/FeatureGates';
import {
  fetchWorkspacePolicyCheck,
  type PolicyCheckSuccess,
} from '../shared/agent-consent/domain/policyClient';
import { MCP_INTENT, MCP_INTENT_ORDER } from './domain/intents';
import {
  compareToolsets,
  getToolsetNames,
  getVisibleToolsetNames,
  parseMcpPolicyData,
} from './domain/policyParse';
import type { McpPolicyIntentGroup } from './domain/policyTypes';
import type { TrelloWorkspace } from './domain/trelloTypes';
import {
  readRecentWorkspaceIds,
  recordConsentedWorkspaceIds,
} from './domain/recentWorkspaceStorage';
import { useUser } from '#definitions/user';
import type { BitbucketWorkspace } from '#definitions/consent-info';
import { useBitbucketWorkspaces } from './domain/useBitbucketWorkspaces';
import { useBitbucketPolicy } from './domain/useBitbucketPolicy';
import { useOrgMatch } from './domain/useOrgMatch';
import type { SiteOrgIndex } from './domain/orgMatch';
import {
  getInitialTrelloWorkspaceSelection,
  normalizeTrelloWorkspaceSelection,
  sortTrelloWorkspacesByName,
} from './domain/trelloWorkspaceSelection';
import {
  classifyScopesWithOverride,
  hasAtlassianScopes,
  hasTrelloInboxPlannerScopes,
  hasTrelloScopes,
  type ScopeClassification,
} from './domain/trelloScopes';
import {
  getCombinedPolicyStatus,
  getOrderedPolicySources,
  getPolicyRequestKey,
  getPolicyStateForRequest,
  getPrimaryPolicyErrorSource,
  IDLE_MCP_POLICIES,
  MCP_POLICY_ERROR_KIND,
  policyReducer,
  type McpPolicies,
  type McpPolicyErrorKind,
  type McpPolicySource,
  type McpPolicyStatus,
} from './domain/policyState';

export { IDLE_MCP_POLICIES };
export type { McpPolicies, McpPolicySource };

/**
 * Sent as `selectedWorkspace` when Trello policy has to be evaluated without a
 * workspace, i.e. the member has none but requested account-level Inbox and
 * Planner scopes.
 */
const ACCOUNT_LEVEL_TRELLO_POLICY_TARGET = '';

export type McpContext = {
  mcpPolicyStatus: McpPolicyStatus;
  mcpPolicyErrorKind?: McpPolicyErrorKind | null;
  /** Display name of the workspace that triggered the error, when relevant. */
  mcpPolicyErrorWorkspaceName?: string | null;
  /**
   * Each policy check on its own, so a selector can report its own failures
   * without picking up another source's, and Accept can ask about one source
   * rather than about whichever error happens to be reported.
   */
  policies: McpPolicies;
  /**
   * Whether the request includes account-level Inbox/Planner scopes, which can
   * be granted even when the user has no Trello workspaces.
   */
  hasTrelloInboxPlannerScopes: boolean;
  /** Intent groups from the API, ordered read → write → search. */
  mcpByIntent: McpPolicyIntentGroup[];
  /** Selected toolset names (raw name from API, e.g. "read_jira"). */
  mcpSelectedToolsetNames: ReadonlySet<string>;
  toggleMcpToolset: (toolsetName: string, selected: boolean) => void;
  /** Scope classification: which product lines (Trello / Atlassian) are in the token. */
  scopeClassification: ScopeClassification;
  trelloRequestedButNoAccess: boolean;
  /** Currently selected Trello workspaces. Only contains multiple values when the multi-select gate is enabled. */
  selectedTrelloWorkspaces: TrelloWorkspace[];
  setSelectedTrelloWorkspaces: (workspaces: TrelloWorkspace[]) => void;
  /** Whether the Trello workspace selector allows more than one workspace. */
  isTrelloWorkspaceMultiSelectEnabled?: boolean;
  /** Whether to render the reworked Trello multi-workspace selector. */
  isTrelloMultiWorkspaceSelectorEnabled?: boolean;
  /**
   * Maximum number of Trello workspaces that can be selected, or null when the
   * selection is unbounded.
   */
  maxSelectableTrelloWorkspaces?: number | null;
  /** Display name of the client requesting consent, when known. */
  clientName?: string | null;
  /** Mock Trello workspaces available for selection (prototype — will be replaced by AGG query). */
  availableTrelloWorkspaces: TrelloWorkspace[];
  /** Whether Trello workspaces are currently being fetched (Relay suspend). */
  trelloWorkspacesLoading: boolean;
  /** True when another page of Trello workspaces is available to fetch. */
  trelloWorkspacesHasMore: boolean;
  /** True while the next page of Trello workspaces is in flight. */
  trelloWorkspacesLoadingMore: boolean;
  /** Requests the next page of Trello workspaces. */
  onLoadMoreTrelloWorkspaces: () => void;
  /**
   * True when the token requested inbox/planner scopes but the selected Trello workspace
   * is outside the user's managing organization, so those scopes are not available.
   */
  inboxPlannerScopesDisabled: boolean;
  /** Whether the Bitbucket workspace selector is enabled (workspaces available). */
  isBitbucketWorkspaceAccessEnabled: boolean;
  /** Bitbucket workspaces available for selection. */
  availableBitbucketWorkspaces: BitbucketWorkspace[];
  /** The selected Bitbucket workspace, or null when none is chosen. */
  selectedBitbucketWorkspace: BitbucketWorkspace | null;
  setSelectedBitbucketWorkspace: (workspace: BitbucketWorkspace | null) => void;
  /** Organization owning the selected site, or null when unknown. */
  siteOrgId: string | null;
  /**
   * Site URL → owning orgId from `/consent/info`. Shared so selectors look a
   * site up here instead of indexing the response themselves.
   */
  siteOrgIndex: SiteOrgIndex;
  /** Organization owning the selected Bitbucket workspace, or null when unknown. */
  bitbucketOrgId: string | null;
  /**
   * Both orgs are known and differ, so the two selections cannot be granted in
   * one consent. Reported under both pickers and blocks Accept.
   */
  hasOrgMismatch: boolean;
  /**
   * Trello workspace ids consented to on previous visits, most recent first.
   * Empty when the multi-workspace selector gate is off or nothing is stored.
   */
  recentTrelloWorkspaceIds?: string[];
  /** Persists the current Trello selection as recent. Called on Accept. */
  recordConsentedTrelloWorkspaces?: () => void;
};

export const ReactMcpContext = createContext<McpContext | null>(null);

export const useMcpContext = () => {
  const context = useContext(ReactMcpContext);
  if (!context) {
    throw new Error('useMcpContext must be used within McpContextProvider');
  }
  return context;
};

function mergeMcpPolicyIntents(
  intentGroups: McpPolicyIntentGroup[],
): McpPolicyIntentGroup[] {
  const merged = new Map<string, McpPolicyIntentGroup>();

  for (const group of intentGroups) {
    const existing = merged.get(group.id);
    if (existing) {
      const existingNames = new Set(
        existing.toolsets.map((toolset) => toolset.name),
      );
      const newToolsets = group.toolsets.filter((toolset) => {
        if (existingNames.has(toolset.name)) {
          return false;
        }
        existingNames.add(toolset.name);
        return true;
      });
      existing.toolsets = [...existing.toolsets, ...newToolsets].sort(
        compareToolsets,
      );
    } else {
      merged.set(group.id, { ...group, toolsets: [...group.toolsets] });
    }
  }

  return MCP_INTENT_ORDER.filter((id) => merged.has(id)).map((id) =>
    merged.get(id)!,
  );
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

const noopLoadMore = () => {};

type McpContextProviderProps = PropsWithChildren<{
  /** Override the list of Trello workspaces (used in tests / Storybook). */
  trelloWorkspaces?: TrelloWorkspace[];
  /** Whether Trello workspaces are currently being fetched (e.g. via Relay suspend). */
  trelloWorkspacesLoading?: boolean;
  /** True when another page of Trello workspaces is available to fetch. */
  trelloWorkspacesHasMore?: boolean;
  /** True while the next page of Trello workspaces is in flight. */
  trelloWorkspacesLoadingMore?: boolean;
  /** Requests the next page of Trello workspaces. */
  onLoadMoreTrelloWorkspaces?: () => void;
  /**
   * Override the previously-consented Trello workspace ids, bypassing
   * localStorage (used in tests / Storybook).
   */
  recentTrelloWorkspaceIds?: string[];
}>;

export const McpContextProvider: FunctionComponent<McpContextProviderProps> = ({
  children,
  trelloWorkspaces,
  trelloWorkspacesLoading = false,
  trelloWorkspacesHasMore = false,
  trelloWorkspacesLoadingMore = false,
  onLoadMoreTrelloWorkspaces = noopLoadMore,
  recentTrelloWorkspaceIds: recentTrelloWorkspaceIdsOverride,
}) => {
  const consentContext = useContext(ReactConsentContext);
  if (!consentContext) {
    throw new Error(
      'McpContextProvider must be used within ConsentContextProvider',
    );
  }

  const { token } = consentContext;
  const info = useInfo();
  const analyticsClient = useAnalytics();
  const { selectedWorkspace } = useSelectedWorkspace();

  const jiraDeleteToolsetsEnabled = useFeatureGate(
    OAuthConsentFeatureGate.MCP_JIRA_DELETE_TOOLSETS,
  );
  const jiraManageToolsetsEnabled = useFeatureGate(
    OAuthConsentFeatureGate.MCP_JIRA_MANAGE_TOOLSETS,
  );
  const [policyState, dispatchPolicy] = useReducer(policyReducer, {
    status: 'idle',
  });
  // Separate policy state for Trello workspace policy check
  const [trelloPolicyState, dispatchTrelloPolicy] = useReducer(policyReducer, {
    status: 'idle',
  });
  const [selectedNames, setSelectedNames] = useState<ReadonlySet<string>>(
    new Set(),
  );

  // Trello workspace state
  const [selectedTrelloWorkspaces, setSelectedTrelloWorkspacesState] = useState<
    TrelloWorkspace[]
  >([]);
  // Tracks whether we've already applied the initial workspace prefill, so a
  // user clearing their selection is not immediately overridden.
  const trelloPrefillAppliedRef = useRef(false);

  const trelloWorkspaceMultiSelectionEnabled = useFeatureGate(
    OAuthConsentFeatureGate.TRELLO_MCP_MULTI_WORKSPACE_SELECTION,
  );

  const trelloMixedWorkspaceEnabled = useFeatureGate(
    OAuthConsentFeatureGate.TRELLO_MCP_MIXED_WORKSPACE,
  );

  // Bitbucket workspace selection (available list, selection state, and
  // stale-selection cleanup) lives in a dedicated hook and is folded into the
  // context value below.
  const {
    isBitbucketWorkspaceAccessEnabled,
    availableBitbucketWorkspaces,
    selectedBitbucketWorkspace,
    setSelectedBitbucketWorkspace,
  } = useBitbucketWorkspaces();

  const selectFetchedToolsets = useCallback((toolsetNames: Set<string>) => {
    setSelectedNames((prev) => {
      const next = new Set(prev);
      for (const name of toolsetNames) next.add(name);
      return next;
    });
  }, []);

  const bitbucketPolicyState = useBitbucketPolicy({
    token,
    isEnabled: isBitbucketWorkspaceAccessEnabled,
    selectedBitbucketWorkspace,
    onToolsetsLoaded: selectFetchedToolsets,
  });

  const multiWorkspaceSelectorEnabled = useFeatureGate(
    OAuthConsentFeatureGate.MULTI_WORKSPACE_SELECTOR,
  );

  const scopeClassification = useMemo<ScopeClassification>(
    () =>
      classifyScopesWithOverride(
        token.scopes ?? [],
        info.hasTrelloProductAccess,
        trelloMixedWorkspaceEnabled,
      ),
    [token.scopes, info.hasTrelloProductAccess, trelloMixedWorkspaceEnabled],
  );

  // Site and Bitbucket must grant within a single organization. Compared only
  // when both pickers are in play: a site-only flow has nothing to disagree
  // with, and a Trello-only flow renders no site picker — its hidden
  // auto-selected workspace is not a user choice, so it must never be reported
  // as conflicting.
  const { siteOrgId, bitbucketOrgId, hasOrgMismatch, siteOrgIndex } =
    useOrgMatch({
      applies:
        isBitbucketWorkspaceAccessEnabled && scopeClassification.hasAtlassian,
      selectedWorkspace,
      selectedBitbucketWorkspace,
    });

  const trelloRequestedButNoAccess = useMemo(() => {
    const scopes = token.scopes ?? [];
    return (
      hasTrelloScopes(scopes) &&
      !hasAtlassianScopes(scopes) &&
      info.hasTrelloProductAccess !== true
    );
  }, [token.scopes, info.hasTrelloProductAccess]);

  const trelloInboxPlannerScopesRequested = useMemo(
    () => hasTrelloInboxPlannerScopes(token.scopes ?? []),
    [token.scopes],
  );

  const isTrelloMultiWorkspaceSelectorEnabled =
    multiWorkspaceSelectorEnabled && !scopeClassification.hasAtlassian;

  const isTrelloWorkspaceMultiSelectEnabled =
    (trelloWorkspaceMultiSelectionEnabled || multiWorkspaceSelectorEnabled) &&
    !scopeClassification.hasAtlassian;

  const clientName = info.client?.name ?? token.clientName ?? null;

  const maxSelectableTrelloWorkspaces = useMemo(() => {
    const limit = info.maxTrelloWorkspaces;
    if (limit == null || !Number.isFinite(limit) || limit <= 0) return null;
    return Math.floor(limit);
  }, [info.maxTrelloWorkspaces]);

  const setSelectedTrelloWorkspaces = useCallback(
    (workspaces: TrelloWorkspace[]) => {
      setSelectedTrelloWorkspacesState(() =>
        normalizeTrelloWorkspaceSelection(
          workspaces,
          isTrelloWorkspaceMultiSelectEnabled,
        ),
      );
    },
    [isTrelloWorkspaceMultiSelectEnabled],
  );

  const user = useUser();
  const accountId = user?.account_id ?? null;

  const recentTrelloWorkspaceIds = useMemo<string[]>(() => {
    if (recentTrelloWorkspaceIdsOverride !== undefined) {
      return recentTrelloWorkspaceIdsOverride;
    }
    if (!isTrelloMultiWorkspaceSelectorEnabled) return [];
    return readRecentWorkspaceIds(accountId);
  }, [
    recentTrelloWorkspaceIdsOverride,
    isTrelloMultiWorkspaceSelectorEnabled,
    accountId,
  ]);

  const recordConsentedTrelloWorkspaces = useCallback(() => {
    if (!isTrelloMultiWorkspaceSelectorEnabled) return;
    recordConsentedWorkspaceIds(
      accountId,
      selectedTrelloWorkspaces.map((workspace) => workspace.id),
    );
  }, [
    isTrelloMultiWorkspaceSelectorEnabled,
    accountId,
    selectedTrelloWorkspaces,
  ]);

  // Trello workspaces: use the override prop (tests / Storybook) or fall back
  // to an empty list. In production the parent tree will wrap with a Suspense
  // boundary and call `useTrelloWorkspaces()` from `domain/getTrelloWorkspaces`
  // to populate this via the `trelloWorkspaces` prop.
  // The full list is exposed rather than one narrowed to the current
  // selection's organization. Workspaces that cannot be combined with the
  // selection are disabled by the selector, so the options keep their position
  // instead of disappearing as soon as something is picked.
  const availableTrelloWorkspaces = useMemo<TrelloWorkspace[]>(() => {
    if (!scopeClassification.hasTrello) return [];
    return sortTrelloWorkspacesByName(trelloWorkspaces ?? []);
  }, [scopeClassification.hasTrello, trelloWorkspaces]);

  // Inbox and Planner are account level rather than workspace level, so when
  // the member belongs to no workspace at all but those scopes were requested,
  // policy is still evaluated once with an empty selectedWorkspace.
  const trelloAccountLevelPolicyOnly =
    availableTrelloWorkspaces.length === 0 && trelloInboxPlannerScopesRequested;
  const trelloPolicyTargets = useMemo(
    () =>
      trelloAccountLevelPolicyOnly
        ? [ACCOUNT_LEVEL_TRELLO_POLICY_TARGET]
        : selectedTrelloWorkspaces.map((workspace) => workspace.id),
    [selectedTrelloWorkspaces, trelloAccountLevelPolicyOnly],
  );
  const trelloPolicyRequestKey =
    scopeClassification.hasTrello && trelloPolicyTargets.length > 0
      ? getPolicyRequestKey(token.raw, trelloPolicyTargets)
      : null;
  const sitePolicyRequestKey = selectedWorkspace
    ? getPolicyRequestKey(token.raw, [selectedWorkspace])
    : null;

  // Effects start the next request after render. Scope stored results to the
  // current selection so a new target can never inherit the previous target's
  // toolsets or successful status during that render.
  const activeSitePolicyState = getPolicyStateForRequest(
    policyState,
    sitePolicyRequestKey,
  );
  const activeTrelloPolicyState = getPolicyStateForRequest(
    trelloPolicyState,
    trelloPolicyRequestKey,
  );
  const orderedPolicySources = useMemo(
    () => getOrderedPolicySources(scopeClassification),
    [scopeClassification],
  );

  // Intent groups the user is allowed to see. Delete and Manage are still
  // rolling out, so each is hidden until its own gate is on.
  const isIntentGroupEnabled = useCallback(
    (intentId: string) => {
      if (intentId === MCP_INTENT.delete) return jiraDeleteToolsetsEnabled;
      if (intentId === MCP_INTENT.manage) return jiraManageToolsetsEnabled;
      return true;
    },
    [jiraDeleteToolsetsEnabled, jiraManageToolsetsEnabled],
  );

  // Merge the site, Trello and Bitbucket policy byIntent arrays.
  // All are ordered read → write → search; merge toolsets within matching intents.
  const byIntent = useMemo<McpPolicyIntentGroup[]>(() => {
    const siteIntents =
      orderedPolicySources.includes('site') &&
      activeSitePolicyState.status === 'success'
        ? activeSitePolicyState.byIntent
        : [];
    const trelloIntents =
      orderedPolicySources.includes('trello') &&
      activeTrelloPolicyState.status === 'success'
        ? activeTrelloPolicyState.byIntent
        : [];
    const bitbucketIntents =
      orderedPolicySources.includes('bitbucket') &&
      bitbucketPolicyState.status === 'success'
        ? bitbucketPolicyState.byIntent
        : [];

    const merged = mergeMcpPolicyIntents([
      ...siteIntents,
      ...trelloIntents,
      ...bitbucketIntents,
    ]).filter((group) => isIntentGroupEnabled(group.id));

    return merged;
  }, [
    activeSitePolicyState,
    activeTrelloPolicyState,
    bitbucketPolicyState,
    isIntentGroupEnabled,
    orderedPolicySources,
  ]);

  // Drop selected toolsets only when they are no longer visible in the merged
  // policy, e.g. after a workspace is unselected or a gate hides an intent.
  useEffect(() => {
    const visibleNames = getVisibleToolsetNames(byIntent);
    setSelectedNames((prev) => {
      const next = new Set([...prev].filter((name) => visibleNames.has(name)));
      return next.size === prev.size ? prev : next;
    });
  }, [byIntent]);

  const { setSelectedWorkspace } = consentContext;

  // In mixed flows (both Atlassian + Trello), reset Trello workspace on site change.
  useEffect(() => {
    if (scopeClassification.hasAtlassian) {
      setSelectedTrelloWorkspaces([]);
    }
  }, [
    selectedWorkspace,
    scopeClassification.hasAtlassian,
    setSelectedTrelloWorkspaces,
  ]);

  useEffect(() => {
    setSelectedTrelloWorkspacesState((current) =>
      normalizeTrelloWorkspaceSelection(
        current,
        isTrelloWorkspaceMultiSelectEnabled,
      ),
    );
  }, [isTrelloWorkspaceMultiSelectEnabled]);

  // Prefill the Trello workspace selection once the workspaces have loaded
  // (Trello-only flow). A single workspace auto-selects; multiple workspaces
  // require user selection. Mixed flows are driven by the Atlassian SiteSelector
  // and reset Trello on site change, so we skip prefill there. The ref guard
  // ensures we only prefill on initial load and never re-apply after the user
  // clears their selection.
  useEffect(() => {
    if (!scopeClassification.hasTrello || scopeClassification.hasAtlassian) {
      return;
    }
    if (trelloPrefillAppliedRef.current) {
      return;
    }
    if (selectedTrelloWorkspaces.length > 0) {
      trelloPrefillAppliedRef.current = true;
      return;
    }
    const initialSelection = getInitialTrelloWorkspaceSelection(
      availableTrelloWorkspaces,
    );
    if (initialSelection.length > 0) {
      trelloPrefillAppliedRef.current = true;
      setSelectedTrelloWorkspaces(initialSelection);
    }
  }, [
    scopeClassification,
    availableTrelloWorkspaces,
    selectedTrelloWorkspaces,
    setSelectedTrelloWorkspaces,
  ]);

  // In Trello-only flows the Atlassian SiteSelector is hidden, so we
  // drive the policy fetch from Trello workspace selection instead.
  // Selecting a Trello workspace auto-selects the (hidden) Atlassian workspace
  // to trigger the policy fetch; clearing it resets the policy.
  useEffect(() => {
    if (!scopeClassification.hasTrello || scopeClassification.hasAtlassian) {
      return; // Only applies to Trello-only flows
    }
    const firstWorkspace = token.workspaces?.[0] ?? null;
    if (
      selectedTrelloWorkspaces.length > 0 &&
      !selectedWorkspace &&
      firstWorkspace
    ) {
      setSelectedWorkspace?.(firstWorkspace);
    } else if (selectedTrelloWorkspaces.length === 0 && selectedWorkspace) {
      setSelectedWorkspace?.(null);
    }
  }, [
    scopeClassification,
    selectedTrelloWorkspaces,
    selectedWorkspace,
    token.workspaces,
    setSelectedWorkspace,
  ]);

  // Fetch policy whenever token or selectedWorkspace changes
  useEffect(() => {
    if (token.serviceType !== 'MCP') return;

    if (!selectedWorkspace) {
      // No workspace selected — either the user hasn't picked one yet,
      // there are none available, or the key is missing from the token.
      // Stay idle so we don't fire a policy fetch that would fail and
      // show a misleading error alongside the SiteSelector's empty-state.
      dispatchPolicy({ type: 'RESET' });
      return;
    }
    const requestKey = getPolicyRequestKey(token.raw, [selectedWorkspace]);

    const callback = token.redirectUrl?.callback ?? '';
    if (!callback) {
      dispatchPolicy({
        type: 'ERROR',
        requestKey,
        errorMessage: null,
        errorKind: MCP_POLICY_ERROR_KIND.missingRedirect,
      });
      analyticsClient.pushTrackEvent({
        actionSubject: 'mcpPolicyCheck',
        action: 'failed',
        source: 'oauthConsentScreen',
        attributes: { reason: MCP_POLICY_ERROR_KIND.missingRedirect },
      });
      return;
    }

    const ac = new AbortController();
    dispatchPolicy({ type: 'LOADING', requestKey });

    void (async () => {
      try {
        const result = await fetchWorkspacePolicyCheck(
          { contextJwt: token.raw, selectedWorkspace },
          ac.signal,
        );

        if (ac.signal.aborted) return;

        if (!result.ok) {
          dispatchPolicy({
            type: 'ERROR',
            requestKey,
            errorMessage: result.errorMessage,
            errorKind: MCP_POLICY_ERROR_KIND.policyFetchFailed,
          });
          analyticsClient.pushTrackEvent({
            actionSubject: 'mcpPolicyCheck',
            action: 'failed',
            source: 'oauthConsentScreen',
            attributes: { reason: MCP_POLICY_ERROR_KIND.policyFetchFailed },
          });
          return;
        }

        if (!result.data.redirectAllowed) {
          dispatchPolicy({
            type: 'ERROR',
            requestKey,
            errorMessage: null,
            errorKind: MCP_POLICY_ERROR_KIND.redirectNotAllowed,
          });
          analyticsClient.pushTrackEvent({
            actionSubject: 'mcpPolicyCheck',
            action: 'failed',
            source: 'oauthConsentScreen',
            attributes: { reason: MCP_POLICY_ERROR_KIND.redirectNotAllowed },
          });
          return;
        }

        const mcpData = parseMcpPolicyData(result.data);
        const newByIntent = mcpData.byIntent;
        dispatchPolicy({
          type: 'SUCCESS',
          requestKey,
          byIntent: newByIntent,
          crossOrgScopesRemoved: mcpData.crossOrgScopesRemoved,
        });
        // Pre-select toolsets
        selectFetchedToolsets(getToolsetNames(newByIntent));
      } catch (e) {
        if (
          (e instanceof DOMException && e.name === 'AbortError') ||
          ac.signal.aborted
        )
          return;
        dispatchPolicy({
          type: 'ERROR',
          requestKey,
          errorMessage: e instanceof Error ? e.message : 'Unexpected error',
          errorKind: MCP_POLICY_ERROR_KIND.unexpected,
        });
      }
    })();

    return () => {
      ac.abort();
    };
  }, [token, selectedWorkspace, analyticsClient, selectFetchedToolsets]);

  // Fetch Trello policy when one or more Trello workspaces are selected.
  // Uses the same /consent/workspace endpoint as Atlassian site policy,
  // passing the Trello workspace ARI as the selectedWorkspace parameter.
  //
  useEffect(() => {
    if (token.serviceType !== 'MCP') return;
    if (!scopeClassification.hasTrello) return;

    if (trelloPolicyRequestKey == null) {
      dispatchTrelloPolicy({ type: 'RESET' });
      return;
    }

    const ac = new AbortController();
    dispatchTrelloPolicy({
      type: 'LOADING',
      requestKey: trelloPolicyRequestKey,
    });

    void (async () => {
      try {
        const results = await Promise.all(
          trelloPolicyTargets.map((selectedTrelloWorkspace) =>
            fetchWorkspacePolicyCheck(
              {
                contextJwt: token.raw,
                selectedWorkspace: selectedTrelloWorkspace,
              },
              ac.signal,
            ),
          ),
        );

        if (ac.signal.aborted) return;

        const failedIndex = results.findIndex((result) => !result.ok);
        const failedResult = failedIndex === -1 ? null : results[failedIndex];
        if (failedResult && !failedResult.ok) {
          const accessRevoked = failedResult.status === 403;
          dispatchTrelloPolicy({
            type: 'ERROR',
            requestKey: trelloPolicyRequestKey,
            errorMessage: failedResult.errorMessage,
            errorKind: accessRevoked
              ? MCP_POLICY_ERROR_KIND.workspaceAccessRevoked
              : MCP_POLICY_ERROR_KIND.policyFetchFailed,
            errorWorkspaceName: accessRevoked
              ? (selectedTrelloWorkspaces[failedIndex]?.displayName ?? null)
              : null,
          });
          return;
        }

        const successfulResults = results.filter(
          (result): result is PolicyCheckSuccess => result.ok,
        );

        if (successfulResults.some((result) => !result.data.redirectAllowed)) {
          dispatchTrelloPolicy({
            type: 'ERROR',
            requestKey: trelloPolicyRequestKey,
            errorMessage: null,
            errorKind: MCP_POLICY_ERROR_KIND.redirectNotAllowed,
          });
          return;
        }

        const parsedPolicyData = successfulResults.map((result) =>
          parseMcpPolicyData(result.data),
        );
        const newByIntent = mergeMcpPolicyIntents(
          parsedPolicyData.flatMap((data) => data.byIntent),
        );
        dispatchTrelloPolicy({
          type: 'SUCCESS',
          requestKey: trelloPolicyRequestKey,
          byIntent: newByIntent,
          crossOrgScopesRemoved: parsedPolicyData.some(
            (data) => data.crossOrgScopesRemoved,
          ),
        });
        // Pre-select Trello toolsets, adding to existing selection
        selectFetchedToolsets(getToolsetNames(newByIntent));
      } catch (e) {
        if (
          (e instanceof DOMException && e.name === 'AbortError') ||
          ac.signal.aborted
        )
          return;
        dispatchTrelloPolicy({
          type: 'ERROR',
          requestKey: trelloPolicyRequestKey,
          errorMessage: e instanceof Error ? e.message : 'Unexpected error',
          errorKind: MCP_POLICY_ERROR_KIND.unexpected,
        });
      }
    })();

    return () => {
      ac.abort();
    };
  }, [
    token,
    selectedTrelloWorkspaces,
    scopeClassification.hasTrello,
    trelloPolicyRequestKey,
    trelloPolicyTargets,
    selectFetchedToolsets,
  ]);

  const toggleMcpToolset = useCallback(
    (toolsetName: string, selected: boolean) => {
      setSelectedNames((prev) => {
        const next = new Set(prev);
        if (selected) next.add(toolsetName);
        else next.delete(toolsetName);
        return next;
      });
    },
    [],
  );

  // Determine if cross-org scopes (inbox/planner) are disabled for the selected Trello workspace.
  // The policy response includes a crossOrgScopesRemoved flag when the selected workspace
  // is outside the user's managing organization.
  const inboxPlannerScopesDisabled = useMemo(() => {
    // Only relevant when Trello scopes are in play and a workspace is selected
    if (
      !scopeClassification.hasTrello ||
      selectedTrelloWorkspaces.length === 0
    ) {
      return false;
    }

    // Check the Trello policy response for crossOrgScopesRemoved flag
    if (activeTrelloPolicyState.status === 'success') {
      return activeTrelloPolicyState.crossOrgScopesRemoved;
    }

    return false;
  }, [
    scopeClassification.hasTrello,
    selectedTrelloWorkspaces,
    activeTrelloPolicyState,
  ]);

  const value = useMemo<McpContext>(() => {
    // The three checks, keyed by source. In a Trello-only flow the hidden site
    // check does not speak for the flow; orderedSources handles that scoping.
    const policies: McpPolicies = {
      site: activeSitePolicyState,
      trello: activeTrelloPolicyState,
      bitbucket: bitbucketPolicyState,
    };
    const combinedPolicyStatus: McpPolicyStatus = getCombinedPolicyStatus(
      policies,
      orderedPolicySources,
    );
    // The single failure being reported, resolved once so the kind, message and
    // source can never describe different errors.
    const errorSource = getPrimaryPolicyErrorSource(
      policies,
      orderedPolicySources,
    );
    const errorState = errorSource ? policies[errorSource] : null;
    const errorKind =
      errorState?.status === 'error' ? errorState.errorKind : null;

    return {
      mcpPolicyStatus: combinedPolicyStatus,
      mcpPolicyErrorKind: errorKind,
      mcpPolicyErrorWorkspaceName:
        errorState?.status === 'error'
          ? (errorState.errorWorkspaceName ?? null)
          : null,
      policies,
      hasTrelloInboxPlannerScopes: trelloInboxPlannerScopesRequested,
      mcpByIntent: byIntent,
      mcpSelectedToolsetNames: selectedNames,
      toggleMcpToolset,
      scopeClassification,
      trelloRequestedButNoAccess,
      selectedTrelloWorkspaces,
      setSelectedTrelloWorkspaces,
      isTrelloWorkspaceMultiSelectEnabled,
      isTrelloMultiWorkspaceSelectorEnabled,
      maxSelectableTrelloWorkspaces,
      clientName,
      availableTrelloWorkspaces,
      trelloWorkspacesLoading,
      trelloWorkspacesHasMore,
      trelloWorkspacesLoadingMore,
      onLoadMoreTrelloWorkspaces,
      inboxPlannerScopesDisabled,
      isBitbucketWorkspaceAccessEnabled,
      availableBitbucketWorkspaces,
      selectedBitbucketWorkspace,
      setSelectedBitbucketWorkspace,
      siteOrgId,
      siteOrgIndex,
      bitbucketOrgId,
      hasOrgMismatch,
      recentTrelloWorkspaceIds,
      recordConsentedTrelloWorkspaces,
    };
  }, [
    activeSitePolicyState,
    activeTrelloPolicyState,
    bitbucketPolicyState,
    trelloInboxPlannerScopesRequested,
    byIntent,
    selectedNames,
    toggleMcpToolset,
    scopeClassification,
    trelloRequestedButNoAccess,
    selectedTrelloWorkspaces,
    setSelectedTrelloWorkspaces,
    isTrelloWorkspaceMultiSelectEnabled,
    isTrelloMultiWorkspaceSelectorEnabled,
    maxSelectableTrelloWorkspaces,
    clientName,
    availableTrelloWorkspaces,
    trelloWorkspacesLoading,
    trelloWorkspacesHasMore,
    trelloWorkspacesLoadingMore,
    onLoadMoreTrelloWorkspaces,
    inboxPlannerScopesDisabled,
    isBitbucketWorkspaceAccessEnabled,
    availableBitbucketWorkspaces,
    selectedBitbucketWorkspace,
    setSelectedBitbucketWorkspace,
    siteOrgId,
    siteOrgIndex,
    bitbucketOrgId,
    hasOrgMismatch,
    recentTrelloWorkspaceIds,
    recordConsentedTrelloWorkspaces,
    orderedPolicySources,
  ]);

  return (
    <ReactMcpContext.Provider value={value}>
      {children}
    </ReactMcpContext.Provider>
  );
};

export default McpContextProvider;
